import React, { useState, useRef, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { usePortfolio } from '../context/PortfolioContext';
import './ChatWidget.css';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedHistory, setHasLoadedHistory] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const { selectedFunds, optimizationResults } = usePortfolio();

  // Load chat history on first open
  const loadHistory = useCallback(async () => {
    if (hasLoadedHistory) return;
    try {
      const response = await api.getChatHistory();
      if (response.data?.messages?.length > 0) {
        setMessages(response.data.messages);
      }
      setHasLoadedHistory(true);
    } catch (err) {
      console.error('Failed to load chat history:', err);
      setHasLoadedHistory(true);
    }
  }, [hasLoadedHistory]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      loadHistory();
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, loadHistory]);

  const handleToggle = () => {
    setIsOpen(prev => !prev);
  };

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    const userMessage = { role: 'user', content: trimmed };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      // Get backtest data from localStorage if available
      let backtestData = null;
      try {
        const stored = localStorage.getItem('allocq_backtestResults');
        if (stored) backtestData = JSON.parse(stored);
      } catch { /* ignore */ }

      const response = await api.sendChatMessage(
        trimmed,
        optimizationResults,
        backtestData,
        selectedFunds
      );

      if (response.data?.success) {
        setMessages(prev => [
          ...prev,
          { role: 'assistant', content: response.data.message }
        ]);
      } else {
        setMessages(prev => [
          ...prev,
          { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }
        ]);
      }
    } catch (err) {
      const errorMsg = err.response?.status === 503
        ? 'Chat is currently unavailable. Please try again later.'
        : err.response?.status === 401
          ? 'Please log in to use the chat.'
          : 'Something went wrong. Please try again.';

      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: errorMsg }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClear = async () => {
    try {
      await api.clearChatHistory();
      setMessages([]);
    } catch (err) {
      console.error('Failed to clear chat:', err);
    }
  };

  // Welcome message shown when no messages
  const welcomeMessage = {
    role: 'assistant',
    content: selectedFunds?.length > 0
      ? `Hi! I'm Q, your portfolio assistant. I can see you've selected ${selectedFunds.length} fund${selectedFunds.length > 1 ? 's' : ''}. ${optimizationResults ? "I can help you understand your optimization results, explain the metrics, or suggest diversification areas." : "Run an optimization and I can help explain the results!"} What would you like to know?`
      : "Hi! I'm Q, your portfolio assistant. I can help you understand portfolio metrics, explain analysis results, and suggest diversification areas. Start by selecting some funds, or ask me anything about how AllocQ works!"
  };

  const displayMessages = messages.length > 0 ? messages : [welcomeMessage];

  return (
    <>
      {/* Floating Q Button */}
      <button
        className={`chat-fab ${isOpen ? 'chat-fab--open' : ''}`}
        onClick={handleToggle}
        title={isOpen ? 'Close chat' : 'Chat with Q'}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
      >
        <span className="chat-fab__icon">
          {isOpen ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          ) : (
            <div className="chat-fab__icon-wrapper">
              <img
                className="chat-fab__q-icon"
                src="/allocq-q-icon.svg"
                alt="Q"
              />
              <div className="chat-fab__shine" />
            </div>
          )}
        </span>
      </button>

      {/* Chat Dialog */}
      {isOpen && (
        <div className="chat-dialog">
          {/* Header */}
          <div className="chat-dialog__header">
            <div className="chat-dialog__header-info">
              <span className="chat-dialog__avatar">
                <svg width="18" height="18" viewBox="0 0 512 512" fill="none">
                  <circle cx="240" cy="240" r="170" stroke="#fff" strokeWidth="32" strokeLinecap="round" fill="none"/>
                  <line x1="358" y1="358" x2="430" y2="430" stroke="#fff" strokeWidth="32" strokeLinecap="round"/>
                  <rect x="148" y="190" width="36" height="120" rx="10" fill="#64E3DA"/>
                  <rect x="206" y="150" width="36" height="160" rx="10" fill="#00C1B2"/>
                  <rect x="264" y="210" width="36" height="100" rx="10" fill="#7A6FF0"/>
                </svg>
              </span>
              <div>
                <h3 className="chat-dialog__title">AllocQ Assistant</h3>
                <span className="chat-dialog__status">Online</span>
              </div>
            </div>
            <button
              className="chat-dialog__clear"
              onClick={handleClear}
              title="Clear chat"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="m19 6-.867 12.142A2 2 0 0 1 16.138 20H7.862a2 2 0 0 1-1.995-1.858L5 6m5 0V4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2"></path>
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div className="chat-dialog__messages">
            {displayMessages.map((msg, i) => (
              <div
                key={i}
                className={`chat-message ${msg.role === 'user' ? 'chat-message--user' : 'chat-message--assistant'}`}
              >
                {msg.role === 'assistant' && (
                  <span className="chat-message__avatar">
                    <svg width="14" height="14" viewBox="0 0 512 512" fill="none">
                      <circle cx="240" cy="240" r="170" stroke="#fff" strokeWidth="36" strokeLinecap="round" fill="none"/>
                      <line x1="358" y1="358" x2="430" y2="430" stroke="#fff" strokeWidth="36" strokeLinecap="round"/>
                      <rect x="148" y="190" width="36" height="120" rx="10" fill="#64E3DA"/>
                      <rect x="206" y="150" width="36" height="160" rx="10" fill="#00C1B2"/>
                      <rect x="264" y="210" width="36" height="100" rx="10" fill="#7A6FF0"/>
                    </svg>
                  </span>
                )}
                <div className="chat-message__bubble">
                  {msg.content.split('\n').map((line, j) => (
                    <React.Fragment key={j}>
                      {line}
                      {j < msg.content.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="chat-message chat-message--assistant">
                <span className="chat-message__avatar">
                    <svg width="14" height="14" viewBox="0 0 512 512" fill="none">
                      <circle cx="240" cy="240" r="170" stroke="#fff" strokeWidth="36" strokeLinecap="round" fill="none"/>
                      <line x1="358" y1="358" x2="430" y2="430" stroke="#fff" strokeWidth="36" strokeLinecap="round"/>
                      <rect x="148" y="190" width="36" height="120" rx="10" fill="#64E3DA"/>
                      <rect x="206" y="150" width="36" height="160" rx="10" fill="#00C1B2"/>
                      <rect x="264" y="210" width="36" height="100" rx="10" fill="#7A6FF0"/>
                    </svg>
                  </span>
                <div className="chat-message__bubble chat-message__typing">
                  <span></span><span></span><span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="chat-dialog__input-area">
            <textarea
              ref={inputRef}
              className="chat-dialog__input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask Q anything..."
              rows={1}
              disabled={isLoading}
            />
            <button
              className="chat-dialog__send"
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              title="Send message"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"></path>
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
